import type { BloodworkAnalysis } from './BloodworkAnalysis';
import type { PanelMarker } from './PanelMarker';

// ─── Markers Outside Optimal: system grouping ──────────────────────────────────
// The MOO ("Markers Outside Optimal") dashboard shows each out-of-optimal marker
// under a body-system header (e.g. "IRON STATUS", "LIVER"). That header is NOT
// stored on the marker — it is derived from which panel getter on
// BloodworkAnalysis lists the marker. A marker's reference-range bar legitimately
// appears in several sections, so many markers are listed in MORE THAN ONE panel
// getter (e.g. TIBC is in both the Iron and the Inflammation panels).
//
// This module owns the single source of truth for resolving a marker to ONE MOO
// header, so the web view (bloodwork-test-analysis.tsx) and the PDF export
// (bloodwork-analysis-pdf.tsx) can never drift apart. The resolution rule is
// "the marker's primary (home) section wins over a cross-cutting aggregator
// section that only borrows it for context" — see buildAttrToSystem.

export interface SystemGroupDef {
  label: string;
  markers: PanelMarker[];
}

export interface SystemGroup {
  systemLabel: string;
  markers: PanelMarker[];
}

// Cross-cutting "aggregator" sections. They pull markers from the primary
// sections to tell a story (Inflammation borrows TIBC/ferritin/HDL/...; the
// Glucose add-on patterns borrow NLR/ALT/cortisol/...). They should label a
// marker only when NO primary section claims it (so hs-CRP/ESR/homocysteine
// still read "Inflammation", and glucose/HbA1c/insulin still read "Glucose").
export const AGGREGATOR_LABELS: ReadonlySet<string> = new Set(['Glucose', 'Inflammation']);

// Markers that live in TWO primary panels, where "last primary listed wins"
// (which correctly sends copper/zinc/vitamin A to Vitamins & Minerals) would
// pick the wrong home. Maps marker attribute → the header it must resolve to.
// Ceruloplasmin sits in both the Iron and the Vitamins & Minerals panels; it is
// grouped with the iron-utilization cluster, so it belongs under Iron Status.
export const PRIMARY_OVERRIDES: Readonly<Record<string, string>> = {
  ceruloplasmin: 'Iron Status',
};

// The ordered list of MOO system groups. Order matters: within the primary tier,
// a later panel overrides an earlier one (this is what sends copper/zinc/vitaminA
// to "Vitamins & Minerals" rather than "Iron"/"Immune"). Keep this in lockstep
// with the per-section panel getters on BloodworkAnalysis.
export function buildSystemGroupDefs(bloodworkAnalysis: BloodworkAnalysis): SystemGroupDef[] {
  return [
    { label: 'Blood Oxygen',        markers: bloodworkAnalysis.getRBCPanelMarkers?.() ?? [] },
    { label: 'Iron Status',         markers: bloodworkAnalysis.getIronPanelMarkers?.() ?? [] },
    { label: 'Immune',              markers: bloodworkAnalysis.getWBCPanelMarkers?.() ?? [] },
    { label: 'Blood Clotting',      markers: bloodworkAnalysis.getPlateletPanelMarkers?.() ?? [] },
    { label: 'Glucose',             markers: bloodworkAnalysis.getGlucosePanelMarkers?.() ?? [] },
    { label: 'Kidney',              markers: bloodworkAnalysis.getKidneyPanelMarkers?.() ?? [] },
    { label: 'Vitamins & Minerals', markers: bloodworkAnalysis.getMineralPanelMarkers?.() ?? [] },
    { label: 'Electrolytes',        markers: bloodworkAnalysis.getElectrolytePanelMarkers?.() ?? [] },
    { label: 'Proteins',            markers: bloodworkAnalysis.getProteinsPanelMarkers?.() ?? [] },
    { label: 'Liver',               markers: bloodworkAnalysis.getLiverPanelMarkers?.() ?? [] },
    { label: 'Lipids',              markers: bloodworkAnalysis.getLipidsPanelMarkers?.() ?? [] },
    { label: 'Inflammation',        markers: bloodworkAnalysis.getInflammationOxidativeStressPanelMarkers?.() ?? [] },
    { label: 'Thyroid',             markers: bloodworkAnalysis.getThyroidPanelMarkers?.() ?? [] },
    // Sodium/potassium stay labeled Electrolytes; only the ratio + adrenal
    // hormones carry the Stress & Adrenal label here.
    { label: 'Stress & Adrenal',    markers: (bloodworkAnalysis.getStressAdrenalPanelMarkers?.() ?? []).filter(pm => pm.attribute !== 'sodium' && pm.attribute !== 'potassium') },
    { label: 'Hormones',            markers: bloodworkAnalysis.getSexHormonePanelMarkers?.() ?? [] },
    { label: 'Prostate',            markers: bloodworkAnalysis.getProstatePanelMarkers?.() ?? [] },
  ];
}

// Resolve each marker attribute to exactly one MOO header.
//   pass 1 — aggregator sections set the fallback label;
//   pass 2 — primary sections overwrite it (later primary still beats earlier);
//   pass 3 — explicit overrides for markers split across two primary sections.
export function buildAttrToSystem(defs: SystemGroupDef[]): Record<string, string> {
  const attrToSystem: Record<string, string> = {};

  defs.forEach(({ label, markers }) => {
    if (!AGGREGATOR_LABELS.has(label)) return;
    markers.forEach(pm => { attrToSystem[pm.attribute] = label; });
  });

  defs.forEach(({ label, markers }) => {
    if (AGGREGATOR_LABELS.has(label)) return;
    markers.forEach(pm => { attrToSystem[pm.attribute] = label; });
  });

  Object.entries(PRIMARY_OVERRIDES).forEach(([attr, label]) => {
    if (attr in attrToSystem) attrToSystem[attr] = label;
  });

  return attrToSystem;
}

// Group an out-of-optimal marker list by resolved system header, ordering the
// groups by their strongest (highest-scoring) marker so the most urgent system
// surfaces first. Unchanged behaviour from the original inline implementation.
export function groupBySystem(
  markers: PanelMarker[],
  scoreOf: (pm: PanelMarker) => number,
  attrToSystem: Record<string, string>,
): SystemGroup[] {
  const groupMap: Record<string, PanelMarker[]> = {};
  const groupBestScore: Record<string, number> = {};
  markers.forEach(pm => {
    const label = attrToSystem[pm.attribute] ?? 'Other';
    const score = scoreOf(pm);
    if (!groupMap[label]) { groupMap[label] = []; groupBestScore[label] = score; }
    else if (score > groupBestScore[label]) { groupBestScore[label] = score; }
    groupMap[label].push(pm);
  });
  return Object.keys(groupMap)
    .sort((a, b) => groupBestScore[b] - groupBestScore[a])
    .map(label => ({ systemLabel: label, markers: groupMap[label] }));
}
